import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '../../stores';
import { useTheme } from '../../contexts/ThemeContext';
import { useSubscription } from '../../hooks/useSubscription';
import { manageMode, manageLabel } from '../../services/subscriptionManagement';
import { accessCardPresentation } from '../../services/accessPresentation';
import { useSettingsPage } from '../../hooks/useSettingsPage';
import SettingsSheetNotifications from './SettingsSheetNotifications';
import {
  TimetableSection, SettingsRow, countCalendarsNeedingAttention, timetableValue,
  GoogleAuthNotice, googleAuthNotice, googleRowError, isSwitchedOffGoogle,
} from '../settings';
import {
  RefreshCw,
  Plus,
  Trash2,
} from 'lucide-react';
import './SettingsSheet.css';
// The sheet's .alm-* row CSS lives in pages/Settings.css, loaded via the eager Settings.tsx import in App.tsx.

/** How the "Sign-in" row names the account's provider; absent → no value. */
const AUTH_PROVIDER_LABEL: Record<string, string> = {
  google: 'Google',
  microsoft: 'Microsoft',
  email: 'Email and password',
};

interface SettingsSheetProps {
  onClose: () => void;
}

/** What Tab can land on inside the sheet, in document order. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), '
  + 'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const SettingsSheet: React.FC<SettingsSheetProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const { tagSunsetStops, setTagSunsetStops } = useTheme();
  const {
    freeAccessEnabled, isPaidSubscriber, handleUpgrade,
    manageSubscription, subscriptionSource,
  } = useSubscription();
  const s = useSettingsPage();
  const accessCard = accessCardPresentation(freeAccessEnabled, isPaidSubscriber);

  const [expanded, setExpanded] = useState<string | null>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  // The latest onClose, so the dialog effect below runs once per open.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Dialog behaviour, the same idiom as Today's event sheet (MobileHome):
  // focus moves in on open and returns to the trigger on close, Escape closes,
  // and Tab cycles inside. aria-modal tells assistive tech the page behind is
  // inert; leaving focus there would make that a lie and strand keyboard users
  // behind a sheet they cannot see.
  useEffect(() => {
    const trigger = document.activeElement as HTMLElement | null;
    sheetRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !sheetRef.current) return;
      const items = Array.from(sheetRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      const inside = !!active && sheetRef.current.contains(active);
      if (e.shiftKey && (active === first || active === sheetRef.current || !inside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (trigger && document.contains(trigger)) trigger.focus();
    };
  }, []);

  useEffect(() => {
    if (user) {
      s.fetchSettings();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // A message opens the footer below the scroll body. Hold the dialog at the
  // height it had without it, so the footer takes its room from the bottom of
  // the scrollport instead of growing a short sheet upward: the controls in
  // view stay where the finger is. Without a message the height is free again.
  // The body's scrollHeight is its natural height; the dialog's own height is
  // that plus the footer, capped by max-height, so the smaller of the two is
  // what the dialog measured before the footer appeared.
  const hasMessage = !!s.message;
  useLayoutEffect(() => {
    const sheet = sheetRef.current;
    const body = bodyRef.current;
    if (!sheet || !body) return;
    if (!hasMessage) {
      sheet.style.height = '';
      return;
    }
    const held = Math.min(body.scrollHeight, sheet.offsetHeight);
    if (held > 0) sheet.style.height = `${held}px`;
  }, [hasMessage]);

  const toggle = (section: string) => {
    setExpanded(prev => prev === section ? null : section);
  };

  const homeLabel = user?.family?.home_city
    ? `${user.family.home_city}${user.family.home_state ? `, ${user.family.home_state}` : ''}`
    : undefined;
  const fullName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '';
  // The same count the gear's mark reads (D14), every provider with health.
  const calendarNeedCount = countCalendarsNeedingAttention(s.googleCalendars, s.subscribedCalendars);
  const googleLostAccess = googleAuthNotice(s.googleCalendars);
  const calendarsValue = calendarNeedCount > 0
    ? `${calendarNeedCount} ${calendarNeedCount === 1 ? 'needs' : 'need'} you`
    : [
        s.googleCalendars.length > 0 && 'Google',
        s.microsoftCalendars.length > 0 && 'Microsoft',
        s.subscribedCalendars.length > 0 && `${s.subscribedCalendars.length} ICS`,
      ].filter(Boolean).join(', ') || 'Not connected';
  const planLabel = freeAccessEnabled ? 'Included' : isPaidSubscriber ? 'Plus' : 'Free';
  const accessCardAction = accessCard.action === 'manage'
    ? manageSubscription
    : accessCard.action === 'upgrade' ? () => handleUpgrade('monthly') : undefined;
  // Spans, not divs: the body may sit inside a <button>, which takes phrasing content only.
  const accessCardBody = (
    <>
      <span className="ss-upgrade-text">
        <span className="ss-upgrade-title">{accessCard.title}</span>
        <span className="ss-upgrade-desc">Unlimited plans, AI chat, models, and briefings</span>
      </span>
      <span className={accessCard.action === 'upgrade' ? 'ss-upgrade-price' : 'ss-upgrade-badge'}>
        {accessCard.badge}
      </span>
    </>
  );

  return (
    <div className="ss-backdrop" onClick={onClose}>
      <div
        ref={sheetRef}
        className="ss-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ss-title"
        tabIndex={-1}
        onClick={e => e.stopPropagation()}
      >
        {/* The only scroll container. The message footer below sits outside
            it, so a message can never cover a control or its focus ring. */}
        <div ref={bodyRef} className="ss-body">
          <div className="ss-handle" />
          <div className="alm-sheet-head">
            <h3 id="ss-title" className="ss-title">Settings</h3>
            <button type="button" className="alm-link" onClick={onClose}>
              Close
            </button>
          </div>

          {/* A contents page (Almanac S6): each row names a setting, shows its
              current value, and opens its section in place. */}
          <div className="alm-settings">
            {/* ─── You (authed) ─── */}
            {user && (
              <>
                <h4 className="alm-set-h">You</h4>
                <div className="alm-set-group">
                  <SettingsRow
                    label="Name and email"
                    value={fullName || user.email}
                    expanded={expanded === 'account'}
                    onToggle={() => toggle('account')}
                    controls="ss-open-account"
                    testId="ss-row-account"
                  />
                  {expanded === 'account' && (
                    <div id="ss-open-account" className="ss-expand-body">
                      <div className="ss-account-row">
                        <span className="ss-account-label">Email</span>
                        <span className="ss-account-value">{user.email}</span>
                      </div>
                      {fullName && (
                        <div className="ss-account-row">
                          <span className="ss-account-label">Name</span>
                          <span className="ss-account-value">{fullName}</span>
                        </div>
                      )}
                      {/* Deleting the account is one level down, away from everything else. */}
                      <div className="alm-account-foot">
                        <button
                          type="button"
                          className="alm-link alm-danger"
                          onClick={() => { onClose(); navigate('/settings/delete-account'); }}
                        >
                          Delete account
                        </button>
                      </div>
                    </div>
                  )}

                  <SettingsRow
                    label="Home"
                    value={homeLabel}
                    expanded={expanded === 'home'}
                    onToggle={() => toggle('home')}
                    controls="ss-open-home"
                    testId="ss-row-home"
                  />
                  {expanded === 'home' && (
                    <div id="ss-open-home" className="ss-expand-body">
                      {homeLabel && (
                        <div className="ss-account-row">
                          <span className="ss-account-label">Location</span>
                          <span className="ss-account-value">{homeLabel}</span>
                        </div>
                      )}
                      <button
                        className="ss-text-btn"
                        onClick={() => { onClose(); navigate('/settings#family'); }}
                      >
                        {homeLabel ? 'Change home' : 'Set home'}
                      </button>
                    </div>
                  )}

                  <SettingsRow
                    label="Sign-in"
                    value={user.auth_provider ? AUTH_PROVIDER_LABEL[user.auth_provider] : undefined}
                    expanded={expanded === 'sign-in'}
                    onToggle={() => toggle('sign-in')}
                    controls="ss-open-sign-in"
                    testId="ss-row-sign-in"
                  />
                  {expanded === 'sign-in' && (
                    <div id="ss-open-sign-in" className="ss-expand-body">
                      {user.has_password ? (
                        <button
                          className="ss-text-btn"
                          onClick={() => { onClose(); navigate('/settings#account'); }}
                        >
                          Change password
                        </button>
                      ) : (
                        <div className="ss-empty-hint">No password on this account</div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* ─── Your days ─── */}
            <h4 className="alm-set-h">Your days</h4>
            <div className="alm-set-group">
              {user && (
                <>
                  <SettingsRow
                    label="Calendars"
                    value={calendarsValue}
                    need={calendarNeedCount > 0}
                    expanded={expanded === 'calendar'}
                    onToggle={() => toggle('calendar')}
                    controls="ss-open-calendar"
                    testId="ss-row-calendar"
                  />
                  {expanded === 'calendar' && (
                    <div id="ss-open-calendar" className="ss-expand-body">
                  {/* Google */}
                  <div className="ss-sub-section">
                    <div className="ss-sub-header">
                      <span className="ss-sub-title">Google Calendar</span>
                      {user.has_google_token && (
                        <button
                          type="button"
                          className="ss-sync-btn"
                          onClick={s.handleSyncGoogle}
                          disabled={s.isSyncing}
                          aria-label="Sync Google Calendar now"
                        >
                          <RefreshCw size={12} className={s.isSyncing ? 'ss-spin' : ''} /> Sync
                        </button>
                      )}
                    </div>
                    {!user.has_google_token && (
                      <button
                        type="button"
                        className="ss-connect-btn"
                        onClick={s.handleConnectGoogle}
                        aria-label="Connect Google Calendar"
                      >
                        Connect
                      </button>
                    )}
                    {googleLostAccess !== null && (
                      <GoogleAuthNotice message={googleLostAccess} onReconnect={s.handleConnectGoogle} />
                    )}
                    {s.googleCalendars.map(cal => {
                      // This calendar's own problem (permission, or gone); the
                      // account-level notice above speaks for a lost grant.
                      const rowError = googleRowError(cal);
                      return (
                      <React.Fragment key={cal.id}>
                      <div className="ss-cal-item">
                        <div className="ss-cal-dot" style={{ background: cal.color || 'var(--accent-primary)' }} />
                        <span className="ss-cal-name">{cal.name}</span>
                        {isSwitchedOffGoogle(cal) && (
                          <span className="gcal-off-label" data-testid={`gcal-off-${cal.id}`}>Off</span>
                        )}
                        {/* A real switch: named for its calendar, its state exposed. */}
                        <button
                          type="button"
                          role="switch"
                          aria-checked={!!cal.is_enabled}
                          aria-label={`Sync ${cal.name}`}
                          className={`ss-toggle ss-toggle--sm ${cal.is_enabled ? 'ss-toggle--on' : ''}`}
                          onClick={() => s.handleToggleGoogleCalendar(cal)}
                        >
                          <span className="ss-toggle-knob" aria-hidden="true" />
                        </button>
                      </div>
                      {rowError !== null && (
                        <div className="ss-cal-error" data-testid={`gcal-error-${cal.id}`}>{rowError}</div>
                      )}
                      </React.Fragment>
                      );
                    })}
                  </div>

                  {/* Microsoft */}
                  <div className="ss-sub-section">
                    <div className="ss-sub-header">
                      <span className="ss-sub-title">Microsoft Calendar</span>
                      {s.microsoftStatus?.connected && (
                        <button
                          type="button"
                          className="ss-sync-btn"
                          onClick={s.handleSyncMicrosoft}
                          disabled={s.isSyncingMicrosoft}
                          aria-label="Sync Microsoft Calendar now"
                        >
                          <RefreshCw size={12} className={s.isSyncingMicrosoft ? 'ss-spin' : ''} /> Sync
                        </button>
                      )}
                    </div>
                    {!s.microsoftStatus?.connected && (
                      <button
                        type="button"
                        className="ss-connect-btn"
                        onClick={s.handleConnectMicrosoft}
                        aria-label="Connect Microsoft Calendar"
                      >
                        Connect
                      </button>
                    )}
                    {s.microsoftCalendars.map(cal => (
                      <div key={cal.id} className="ss-cal-item">
                        <div className="ss-cal-dot" style={{ background: cal.color || 'var(--accent-primary)' }} />
                        <span className="ss-cal-name">{cal.name}</span>
                        {/* A real switch: named for its calendar, its state exposed. */}
                        <button
                          type="button"
                          role="switch"
                          aria-checked={!!cal.is_enabled}
                          aria-label={`Sync ${cal.name}`}
                          className={`ss-toggle ss-toggle--sm ${cal.is_enabled ? 'ss-toggle--on' : ''}`}
                          onClick={() => s.handleToggleMicrosoftCalendar(cal)}
                        >
                          <span className="ss-toggle-knob" aria-hidden="true" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* ICS/iCal */}
                  <div className="ss-sub-section">
                    <div className="ss-sub-header">
                      <span className="ss-sub-title">ICS / iCal Feeds</span>
                      {/* The add form lives on the Settings page; the sheet has no room
                          for it, so Add goes there with the form already open. */}
                      <button
                        type="button"
                        className="ss-sync-btn"
                        aria-label="Add calendar feed"
                        onClick={() => { onClose(); navigate('/settings#add-calendar'); }}
                      >
                        <Plus size={12} /> Add
                      </button>
                    </div>
                    {s.subscribedCalendars.map(cal => (
                      <React.Fragment key={cal.id}>
                        <div className="ss-cal-item">
                          <div className="ss-cal-dot" style={{ background: cal.color || 'var(--accent-primary)' }} />
                          <span className="ss-cal-name">{cal.name}</span>
                          {cal.sync_status === 'paused' && (
                            <button
                              type="button"
                              className="ss-sync-btn"
                              title="Retry this feed"
                              aria-label={`Retry ${cal.name}`}
                              onClick={() => s.handleRetryCalendar?.(cal)}
                            >
                              Retry
                            </button>
                          )}
                          <button
                            type="button"
                            className="ss-remove-btn"
                            aria-label={`Remove ${cal.name}`}
                            onClick={() => s.handleRemoveCalendar(cal)}
                          >
                            <Trash2 size={12} aria-hidden="true" />
                          </button>
                        </div>
                        {cal.sync_status === 'paused' && !!cal.last_error && (
                          <div className="ss-cal-error">{cal.last_error}</div>
                        )}
                      </React.Fragment>
                    ))}
                    {s.subscribedCalendars.length === 0 && (
                      <div className="ss-empty-hint">No ICS feeds added</div>
                    )}
                  </div>
                    </div>
                  )}

                  {/* Notifications: S7 owns this row's insides. */}
                  <SettingsSheetNotifications
                    expanded={expanded}
                    toggle={toggle}
                    s={s}
                  />
                </>
              )}

              <SettingsRow
                label="Timetable"
                value={timetableValue(tagSunsetStops)}
                expanded={expanded === 'sunset'}
                onToggle={() => toggle('sunset')}
                controls="ss-open-sunset"
                testId="ss-row-sunset"
              />
              {expanded === 'sunset' && (
                <div id="ss-open-sunset" className="ss-expand-body">
                  <TimetableSection
                    tagSunsetStops={tagSunsetStops}
                    onTagSunsetChange={setTagSunsetStops}
                  />
                </div>
              )}
            </div>

            {/* ─── VizDay ─── */}
            <h4 className="alm-set-h">VizDay</h4>
            <div className="alm-set-group">
              {user && (
                <>
                  <SettingsRow
                    label="Plus"
                    value={planLabel}
                    expanded={expanded === 'plus'}
                    onToggle={() => toggle('plus')}
                    controls="ss-open-plus"
                    testId="ss-row-plus"
                  />
                  {expanded === 'plus' && (
                    <div id="ss-open-plus" className="ss-expand-body">
              {accessCardAction ? (
                // An actionable card is a native button: a keyboard reaches it
                // and Enter or Space runs it.
                <button
                  type="button"
                  className="ss-upgrade-card"
                  onClick={accessCardAction}
                  aria-label={accessCard.action === 'manage'
                    ? `${accessCard.title}: manage subscription`
                    : undefined}
                  aria-describedby={isPaidSubscriber ? 'ss-manage-note' : undefined}
                >
                  {accessCardBody}
                </button>
              ) : (
                <div className="ss-upgrade-card ss-upgrade-card--static">
                  {accessCardBody}
                </div>
              )}
              {/* Existing subscribers still need a path to cancel their billing. */}
              {isPaidSubscriber && (
                <div id="ss-manage-note" className="ss-manage-note">
                  {manageMode(subscriptionSource) === 'stripe_portal' &&
                    (freeAccessEnabled
                      ? 'An older Stripe subscription is still billing — tap above to cancel it. You will keep full access.'
                      : 'Billing managed through Stripe — tap above to update or cancel.')}
                  {manageMode(subscriptionSource) === 'app_store' &&
                    (freeAccessEnabled
                      ? `${manageLabel(subscriptionSource)} is still billing — tap above to cancel it. You will keep full access.`
                      : `${manageLabel(subscriptionSource)} — tap above to open your store subscriptions.`)}
                  {manageMode(subscriptionSource) === 'support' && (
                    <>{freeAccessEnabled
                      ? 'An older subscription is active. You can cancel without losing access; contact '
                      : 'Active. To make changes, contact '}
                      <a href="mailto:arnold@vizday.com">arnold@vizday.com</a>.</>
                  )}
                </div>
              )}
                    </div>
                  )}
                </>
              )}

              <SettingsRow
                label="About"
                value="VizDay v2.0"
                expanded={expanded === 'about'}
                onToggle={() => toggle('about')}
                controls="ss-open-about"
                testId="ss-row-about"
              />
              {expanded === 'about' && (
                <div id="ss-open-about" className="ss-expand-body">
                  <div className="ss-row-desc">VizDay v2.0 · VizDNA LLC</div>
                  <button className="ss-text-btn" onClick={() => { onClose(); navigate('/terms'); }}>
                    Terms of Service
                  </button>
                  <button className="ss-text-btn" onClick={() => { onClose(); navigate('/privacy'); }}>
                    Privacy Policy
                  </button>
                  <button
                    className="ss-text-btn"
                    onClick={() => { window.open('mailto:feedback@vizday.com?subject=VizDay Feedback', '_blank'); onClose(); }}
                  >
                    Send Feedback
                  </button>
                </div>
              )}
            </div>

            {/* ─── Sign out: a plain link at the bottom (authed) ─── */}
            {user && (
              <div className="alm-signout">
                <button
                  type="button"
                  className="alm-link"
                  onClick={() => { s.handleSignOut(); onClose(); }}
                >
                  Sign out
                </button>
              </div>
            )}

            {/* ─── Unauthed CTA ─── */}
            {!user && (
              <div className="ss-section">
                <button className="ss-signup-btn" onClick={() => { onClose(); navigate('/login'); }}>
                  Sign up — it's free
                </button>
              </div>
            )}
          </div>
        </div>

        {/* The outcome of the last action (sync, toggle, remove, retry). The
            hook only holds it in state, so a sheet that does not render it
            lets a failure end with no explanation. A footer outside the scroll
            body: zero height when empty, and never over a control. Errors
            interrupt as an alert; confirmations go into one polite live region
            that is in the DOM from mount, so its text is announced when it
            arrives. */}
        <div className="ss-footer">
          <div className="ss-status" role="status" aria-live="polite">
            {s.message && s.message.type !== 'error' && (
              <p className="ss-message">{s.message.text}</p>
            )}
          </div>
          {s.message && s.message.type === 'error' && (
            <p className="ss-message ss-message--error" role="alert">
              {s.message.text}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsSheet;
